import PropTypes from 'prop-types';
import { WarningIcon, SuccessCircle } from '../../assets/icons';
import Spinner from '../common/Spinner';
import useModalClose from '../../hooks/useModalClose';
import { useState, useEffect } from 'react';
import { getAllAssignments, completeAssignment } from '../../services/vehicleService';

const CompleteAssignmentModal = ({ isOpen, onClose, onSuccess, assignmentId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAssignment, setIsLoadingAssignment] = useState(false);
  const [error, setError] = useState(null);
  const [scaleInfo, setScaleInfo] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [assignment, setAssignment] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useModalClose(isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      // Set current date in YYYY-MM-DD format
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];
      setCurrentDate(formattedDate);
      
      // Reset form state
      setScaleInfo('');
      setError(null);
      setIsSuccess(false);
      
      // Fetch assignment data
      if (assignmentId) {
        fetchAssignmentData();
      }
    }
  }, [isOpen, assignmentId]);

  const fetchAssignmentData = async () => {
    try {
      setIsLoadingAssignment(true);
      setError(null);

      const data = await getAllAssignments({ id: assignmentId });

      if (data.status === 200 && data.assignments && data.assignments.length > 0) {
        const assignment = data.assignments[0];
        setAssignment(assignment);
        
        // Pre-fill scale information if it exists
        if (assignment.scaleInformation) {
          setScaleInfo(assignment.scaleInformation.toString());
        }
      } else {
        setError('Görevlendirme bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Failed to fetch assignment:', error);
      setError('Görevlendirme bilgileri alınamadı.');
    } finally {
      setIsLoadingAssignment(false);
    }
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    // Only allow numbers
    if (/^\d*$/.test(value)) {
      setScaleInfo(value);
    }
  };

  const handleSubmit = async () => {
    // Validate input
    if (!scaleInfo || scaleInfo.trim() === '') {
      setError('Lütfen kantar bilgisini giriniz');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call the API to complete the assignment
      await completeAssignment(assignmentId, scaleInfo);

      setIsSuccess(true);
      
      // Notify parent component with a slight delay to ensure our state updates first
      setTimeout(() => {
        onSuccess(assignmentId, scaleInfo);
      }, 100);
    } catch (error) {
      console.error('Failed to complete assignment:', error);
      setError(error.message || 'Görevlendirme tamamlanırken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content complete-assignment-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{assignment && assignment.scaleInformation ? 'Kantar Bilgisini Düzenle' : 'Görevlendirmeyi Tamamla'}</h3>
        </div>

        {isLoading || isLoadingAssignment ? (
          <div className="loading-state">
            <Spinner size="large" />
            <h3>{isLoadingAssignment ? 'Görevlendirme bilgileri yükleniyor...' : 'Görevlendirme tamamlanıyor...'}</h3>
          </div>
        ) : isSuccess ? (
          <div className="success-state">
            <SuccessCircle />
            <h3>{assignment && assignment.scaleInformation ? 'Kantar bilgisi başarıyla güncellendi.' : 'Görevlendirme başarıyla tamamlandı.'}</h3>
            <button type="button" onClick={onClose} className="close-button">
              Kapat
            </button>
          </div>
        ) : (
          <>
            <div className="modal-body">
              <div className='complete-assignment-warning-text'>
                <p>Kantar bilgilerini girmeden görevlendirme tamamlanmamaktadır. Lütfen kantardaki gördüğünüz <span>kilogram değeri</span> cinsinden giriniz.</p>
                <p>Kantar bilgisini düzenlemek isterseniz ilk girdiğiniz zamandan itibaren <span>bir saat içinde düzenleyebilirsiniz.</span></p>
              </div>

              <div className="form-group">
                <div className="date-input-container">
                  <label htmlFor="completeDate">Tarih</label>
                  <input
                    type="date"
                    id="completeDate"
                    value={currentDate}
                    disabled
                  />
                </div>
                <div className="input-container">
                  <label htmlFor="scaleInfo">Kantar Bilgisi (kg)</label>
                  <input
                    type="text"
                    id="scaleInfo"
                    value={scaleInfo}
                    onChange={handleInputChange}
                    placeholder="Kantar değerini giriniz"
                  />
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={onClose}
              >
                İptal
              </button>
              <button 
                type="button" 
                className="complete-button"
                onClick={handleSubmit}
              >
                {assignment && assignment.scaleInformation 
                  ? 'Kantar Bilgisini Güncelle' 
                  : 'Kantar Bilgisini Kaydet ve Görevlendirmeyi Tamamla'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

CompleteAssignmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  assignmentId: PropTypes.string.isRequired
};

export default CompleteAssignmentModal; 